import styled, { css, keyframes } from "styled-components";
import { colors } from "../../styles/colors";
import { theme } from "../../styles/theme";

// Keyframes for animations
const ulOptionsClose = keyframes`
  0% {
    box-shadow: 1px 1px 4px 1px rgba(0, 0, 0, 0.4);
  }
  99% {
    box-shadow: 1px 1px 4px 1px rgba(0, 0, 0, 0.4);
  }
  100% {
    box-shadow: none;
  }
`;

const ulOptionsCloseBorder = keyframes`
  0% {
    border: 1px solid black;
  }
  99% {
    border: 1px solid black;
  }
  100% {
    border: none;
  }
`;

interface SelectContainerProps {
  $theme?: "modern" | "legacy";
  $isLoading?: boolean;
  $disabled?: boolean;
  $hasValue?: boolean;
}

export const SelectContainer = styled.div<SelectContainerProps>`
  position: relative;

  ${({ $isLoading }) =>
    $isLoading &&
    css`
      & > div.select_input::before {
        display: none;
      }
    `}

  ${({ $disabled }) =>
    $disabled &&
    css`
      pointer-events: none;
      & > div.select_input {
        background-color: ${colors.unabled};
        pointer-events: inherit;
      }
    `}

  ${({ $hasValue }) =>
    !$hasValue &&
    css`
      & > div.select_input > button.--clear {
        opacity: 0;
      }
    `}

  & > label {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    width: 100%;
  }
`;

interface SelectInputProps {
  $theme?: "modern" | "legacy";
  $isLoading?: boolean;
}

export const SelectInput = styled.div<SelectInputProps>`
 appearance: active;
     display: flex;
     background: #ffffff;
     border: 1px solid #d2d3e5;
     box-sizing: border-box;
     border-radius: 8px;
     font-size: 1rem;
     margin-top: 5px;
     display: flex;
     align-items: center;
     padding: 12px;
     width: 100%;
     max-height: 47px;
     transition: all 0.3s ease;

     background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
   background-position: right 1rem center;
  
-webkit-appearance: none; 
-moz-appearance: none; 
     &:focus {
       border-color: ${theme.colors.primary};
       outline: none;
       background-color: #fffbf7;
       box-shadow: 0 0 0 3px rgba(236, 103, 38, 0.1);
     }
 
     &:focus-visible {
       outline: none;
     }
 
     &:hover {
       border-color: ${theme.colors.primary};
     }
 
     &:disabled {
       background-color: #f5f5f5;
       cursor: not-allowed;
     }
`;

interface SelectOptionsContainerProps {
  $theme?: "modern" | "legacy";
  $forceMobile?: boolean;
}

export const SelectOptionsContainer = styled.div<SelectOptionsContainerProps>`
  z-index: 20000;

  ${({ $forceMobile }) => {
    const mobileStyles = css`
      position: relative;

      & > div.overlay {
        align-items: center;
        background-color: rgba(0, 0, 0, 0.2);
        display: flex;
        height: 100vh;
        justify-content: center;
        left: 0;
        opacity: 1;
        position: fixed;
        transition: opacity 0.3s ease, visibility 0.3s ease, background-color 0.3s;
        top: 0;
        visibility: visible;
        width: 100vw;
        z-index: 20000;

        &:not(:has(ul.options.--is_open)) {
          opacity: 0;
          visibility: hidden;
        }

        & > ul.options {
          width: 95vw;
          margin-top: 0;
          max-width: 450px;
        }
      }
    `;

    if ($forceMobile) {
      return mobileStyles;
    }

    return css`
      @media screen and (max-width: 720px) {
        ${mobileStyles}
      }
    `;
  }}

  ${({ $theme }) =>
    $theme === "legacy" &&
    css`
      & > div.overlay > ul.options {
        animation: ${ulOptionsCloseBorder} 0.3s forwards;

        &.--is_open {
          animation: none;
          z-index: 20001;
          border: 1px solid black;
        }

        & > li.--search {
          border-bottom: 1px solid rgba(0, 0, 0, 0.4);
          padding: 0.4rem 0 0.2rem 0.2rem;
          box-shadow: 1px 1px 4px 1px rgba(0, 0, 0, 0.2);
        }

         & > li.--search > input {
          padding: 0.15rem 0.5rem;
          border: none;
          outline: none;
          background-color: transparent;
          width: 100%;
          box-sizing: border-box;
          font-size: 14px;
         }

        & > li:not(.--search) {
          padding: 0.15rem 1rem;
        }
      }
    `}

  ${({ $theme }) =>
    $theme === "modern" &&
    css`
      & > div.overlay > ul.options {
        animation: ${ulOptionsClose} 0.3s forwards;
        border-bottom-left-radius: 2px;
        border-bottom-right-radius: 2px;
        margin-top: 0.2rem;

        &.--is_open {
          animation: none;
          box-shadow: 1px 1px 4px 1px rgba(0, 0, 0, 0.4);
        }

        & > li.--search {
          border-bottom: 1px solid rgba(0, 0, 0, 0.4);
          padding: 0.4rem 0 0.2rem 0.2rem;
          box-shadow: 1px 1px 4px 1px rgba(0, 0, 0, 0.2);
        }

        & > li:not(.--search) {
          padding: 0.3rem 0.5rem;
        }
      }
    `}

  & > div.overlay > ul.options {
    background-color: white;
    list-style: none;
    max-height: 0px;
    overflow: auto;
    position: absolute;
    transition: max-height 0.3s, opacity 0.3s;
    width: 100%;
    z-index: 20001;

    &.--is_open {
      max-height: 336px;
    }

    & > li.--search {
      background-color: white;
      position: sticky;
      top: 0;

      & > input {
        outline: none;
        width: 100%;
      }
    }

    & > li:not(.--search) {
      align-content: center;
      cursor: pointer;
      font-size: 14px;
      height: 31px;
      overflow: hidden;
      text-overflow: ellipsis;
      transition: background-color 0.1s, color 0.1s, height 0.2s, padding 0.2s;
      user-select: none;
      white-space: nowrap;
      width: 100%;

      & > * {
        max-height: 100%;
      }

      &.--hidden {
        height: 0px;
        padding: 0;
      }

      &:hover {
        background-color: ${colors.primary};
        color: white;
      }

      &.--is_selected {
        background-color: ${colors.primaryDark};
      }
    }
  }
`;

export const SelectErrorMessage = styled.p`
  color: #dc3545;
  font-size: 0.875rem;
`;